import { Calendar, DollarSign, Layers, Package, Store, Target } from "lucide-react";
import useCurrentPlan from "../../../../hooks/useCurrentPlan";
import MetaCard from "../../components/atoms/metaCard";

function calcWeeks(from: string | undefined, to: string | undefined): number | null {
    if (!from || !to) return null;
    const a = new Date(from);
    const b = new Date(to);
    if (isNaN(a.getTime()) || isNaN(b.getTime()) || b <= a) return null;
    return Math.round((b.getTime() - a.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

const ScenarioDefinition: React.FC = () => {
    const { headingData, currentPlanData, planStartDate, planEndDate } = useCurrentPlan();

    const weeks = calcWeeks(planStartDate, planEndDate);

    return (
        <div className="d-flex flex-column gap-2 min-w-0">
            <div>
                <p className="small text-uppercase fw-semibold !k-text-subtle mb-1 letter-spacing-wide" style={{ fontSize: "11px" }}>
                    Scenario Definition
                </p>
                <p className="small fw-normal !k-text-subtle mb-0" style={{ fontSize: "12px" }}>
                    Simulation configuration used to generate this plan
                </p>
            </div>

            {/* Row 1 — Retailer / Planning Level / Timeframe */}
            <div className="simulator-plan-grid simulator-plan-grid--scenario-primary">
                <div className="d-flex min-w-0">
                    <MetaCard
                        icon={<Store size={14} />}
                        label="Retailer"
                        value={headingData?.retailers ?? ""}
                    />
                </div>
                <div className="d-flex min-w-0">
                    <MetaCard
                        icon={<Layers size={14} />}
                        label="Planning Level"
                        value={currentPlanData?.planningLevel ?? ""}
                    />
                </div>

                {/* Timeframe card — plan date range */}
                <div className="d-flex min-w-0">
                    <div className="scenario-meta-card rounded-3 px-3 d-flex flex-column h-100 w-100">
                        <div className="d-flex align-items-center gap-1 text-secondary mb-1">
                            <Calendar className="flex-shrink-0" size={14} />
                            <span className="scenario-meta-label text-uppercase fw-semibold">Timeframe</span>
                            {weeks && (
                                <span className="badge rounded-pill ms-auto px-2 py-1 fw-bold scenario-weeks-badge">
                                    {weeks}
                                    w
                                </span>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-1 min-w-0">
                            <input
                                type="date"
                                readOnly
                                value={planStartDate}
                                className="form-control form-control-sm border-0 bg-transparent p-0 small fw-semibold shadow-none min-w-0"
                                style={{ colorScheme: "light" }}
                            />

                            <span className="text-secondary small flex-shrink-0">→</span>

                            <input
                                type="date"
                                readOnly
                                value={planEndDate}
                                className="form-control form-control-sm border-0 bg-transparent p-0 small fw-semibold shadow-none min-w-0"
                                style={{ colorScheme: "light" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 2 — Selection / Goal / Budget */}
            <div className="simulator-plan-grid simulator-plan-grid--scenario-secondary">
                <div className="d-flex min-w-0">
                    <MetaCard
                        icon={<Package size={14} />}
                        label="Selection"
                        value={headingData?.skuName ?? headingData?.subBrandName ?? ""}
                    />
                </div>
                <div className="d-flex min-w-0">
                    <MetaCard
                        icon={<Target size={14} />}
                        label="Goal"
                        value={headingData?.objective ?? ""}
                    />
                </div>
                <div className="d-flex min-w-0">
                    <MetaCard
                        icon={<DollarSign size={14} />}
                        label="Budget"
                        value={currentPlanData?.budget ?? ""}
                    />
                </div>
            </div>
        </div>
    );
};

export default ScenarioDefinition;
